import { useState, useEffect } from "react";
import { CircularProgress, Box } from "@mui/material";
import { CustomGrid } from "../../components/tableComponent";
import pemdingLeaguesIcon from "../../styles/images/pendingLeagueIcon.svg";
import activeLeagueIcon from "../../styles/images/activeLeagueIcon.svg";
import { useNavigate } from "react-router-dom";
import getLeagues from "../../services/getUsers";

const MyLeaguesTable = ({ refetch }) => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortModel, setSortModel] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);

  // Column definitions
  const columns = [
    {
      field: "league_name",
      headerName: "Name",
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => {
        return (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            maxWidth: '100%',
            overflow: 'hidden'
          }}>
            <img
              src={params.row.league_status === "Upcoming" ? pemdingLeaguesIcon : activeLeagueIcon}
              alt="league"
              style={{ flexShrink: 0 }}
            />
            <p
              className="league-name-cell"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/my_leagues/${params.row.league_id}`);
              }}
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {params.row.league_name}
            </p>
          </div>
        );
      },
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <div
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%'
          }}
          title={params.value || 'No description available'}
        >
          {params.value || 'No description available'}
        </div>
      )
    },
    {
      field: "total_seasons",
      headerName: "Seasons",
      flex: 0.5,
      minWidth: 100,
    },
    {
      field: "total_participants",
      headerName: "Participants",
      flex: 0.5,
      minWidth: 100,
    },
  ];

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getLeagues();
      setData(
        response.data.conducted_leagues.map((ele, idx) => ({ id: idx, ...ele }))
      );
      setTotalRecords(response.data.conducted_leagues.length);
    } catch (err) {
      setError("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchData();
  }, [refetch]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0);
  };

  const handleSortChange = (newSortModel) => {
    setSortModel(newSortModel);
  };

  const handleRowSelectionChange = (newSelection) => {
    setSelectedRows(newSelection);
  };

  const handleRowClick = (params) => {
    console.log("Row clicked:", params.row);
  };

  const handleRefresh = () => {
    fetchData();
  };

  // Loading state
  if (loading && !data.length) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%',
      overflow: 'hidden',
      '& .MuiDataGrid-root': {
        border: 'none',
        maxWidth: '100vw',
        overflowX: 'hidden',
      },
      '& .MuiDataGrid-cell': {
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
      '& .MuiDataGrid-columnHeaders': {
        maxWidth: '100%',
        overflow: 'hidden',
      },
      '& .MuiDataGrid-virtualScroller': {
        overflow: 'auto !important',
        maxWidth: '100%',
      },
      '@media (max-width: 600px)': {
        '& .MuiDataGrid-columnHeaders': {
          fontSize: '14px',
        },
        '& .MuiDataGrid-cell': {
          fontSize: '13px',
          padding: '8px',
        },
      }
    }}>
      <CustomGrid
        columns={columns}
        data={data}
        loading={loading}
        perPageCount={pageSize}
        currentPage={page}
        totalPages={totalRecords}
        isServerPagination={false}
        onChangeRecords={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onSortModelChange={handleSortChange}
        onRowClick={handleRowClick}
        onRowSelectionModelChange={handleRowSelectionChange}
        rowHeight={56}
        selectedCount={selectedRows.length}
        selectionModel={selectedRows}
        searchBar={null}
        disableSorting={true}
        sx={{
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
        }}
      />
    </Box>
  );
};

export default MyLeaguesTable;
